import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';

import { API_BASE_URL, API_SCOPE, loginRequest } from '../auth/config';

/**
 * The API client.
 *
 * One place acquires the token and one place classifies the failure. The classification
 * matters more than it looks: a 401 and a 403 mean very different things here and the fix is
 * different for each. 401 is "the token is wrong or expired" and is recoverable by
 * re-authenticating. 403 is "your Entra app role is insufficient" and is not — no amount of
 * retrying changes it, and the user needs to be told which role they hold and which is
 * required rather than being bounced back through a login that will succeed and still fail.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, message: string, detail = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  /** Insufficient role. Retrying will not help. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Nothing the user can do; worth retrying. */
  get isTransient(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

let client: IPublicClientApplication | null = null;

/** Called once from main.tsx after MSAL initialises. */
export function attachMsal(instance: IPublicClientApplication): void {
  client = instance;
}

async function accessToken(): Promise<string> {
  if (!client) throw new ApiError(0, 'The authentication client is not ready yet');

  const account = client.getAllAccounts()[0];
  if (!account) throw new ApiError(401, 'Not signed in');

  try {
    const result = await client.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return result.accessToken;
  } catch (error) {
    // The refresh token has expired or consent was revoked. A redirect is the only way
    // forward; it navigates away, so nothing after it runs.
    if (error instanceof InteractionRequiredAuthError) {
      await client.acquireTokenRedirect({ ...loginRequest, account });
      throw new ApiError(401, 'Re-authentication required');
    }
    throw new ApiError(401, 'Could not acquire an access token', String(error));
  }
}

/**
 * Pull a useful message out of an error response.
 *
 * The backend raises `Forbidden` with text that names the role held and the role required —
 * that string is the single most useful thing on the screen when access is denied, so it is
 * surfaced rather than replaced with a generic "Forbidden".
 */
async function errorFrom(response: Response): Promise<ApiError> {
  let detail = '';
  try {
    const body = (await response.json()) as { message?: string; error?: string; detail?: string };
    detail = body.message ?? body.error ?? body.detail ?? '';
  } catch {
    // A non-JSON error body (a gateway 502, say) has nothing to extract.
  }

  const fallback: Record<number, string> = {
    401: 'Your session has expired',
    403: 'Your Entra app role does not permit this',
    404: 'Not found',
    409: 'This conflicts with the current state of the record',
    429: 'Rate limited — too many requests',
  };

  return new ApiError(
    response.status,
    detail || fallback[response.status] || `Request failed (${response.status})`,
    detail,
  );
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const token = await accessToken();

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // Distinguished from an HTTP error: status 0 means the request never arrived, which is a
    // network or CORS problem rather than anything the API said.
    if (signal?.aborted) throw error;
    throw new ApiError(0, 'Could not reach the API', String(error));
  }

  if (!response.ok) throw await errorFrom(response);

  // An empty body is a normal success here, not just on 204: `enrich` and `analyse` start a
  // Step Functions execution and answer 202 with nothing in it. Calling `response.json()` on
  // that throws a parse error, which would surface as a failed mutation for work that was in
  // fact accepted — so the body is checked before it is parsed rather than after it fails.
  if (response.status === 204 || response.status === 205) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
